import { useMemo } from 'react';
import { WithEnterpriseId, WithServiceId } from '@mews-commander/types';
import { OverlayedLoader } from '@mews-commander/ui';
import { Position, ZIndex } from '@optimus-web/core';
import { LocalizedProperty, lt } from '@mews/framework';
import { Popover, PopoverProps } from '@mews/b2b-ui';
import { useSellLimitsMatrixData } from '../../queries';
import { FilterFormValues } from '../../schemas';
import { useViewTypeName } from '../../hooks';
import { getFilterViewResourceParams } from '../../utils';
import { FilterValuesContextProvider } from '../../FilterValuesContext';
import { PopoverContent } from './PopoverContent';
import { DIALOG_PORTAL_CONTAINER_ID } from './constants';

const allResourcesCategoryId = null;

type DailyLimitsPopoverProps = Pick<PopoverProps, 'anchorElement'> &
    WithServiceId &
    WithEnterpriseId & {
        closePopover: () => void;
        filterValues: FilterFormValues;
        resourceCategoryId?: string | null;
        timeUnit: string;
    };

export const DailyLimitsPopover = ({
    closePopover,
    enterpriseId,
    filterValues,
    resourceCategoryId,
    serviceId,
    timeUnit,
    ...other
}: DailyLimitsPopoverProps) => {
    const { data, isFetching: isDataFetching } = useSellLimitsMatrixData({ filterValues, serviceId });
    const resourceCategoryData = useMemo(
        () => data?.find((source) => source.resourceCategoryId === resourceCategoryId),
        [data, resourceCategoryId]
    );
    const allResourcesCategoryData = useMemo(
        () => data?.find((source) => source.resourceCategoryId === allResourcesCategoryId),
        [data]
    );

    const { name: viewTypeName, names: viewTypeNames } = useViewTypeName({
        view: filterValues.view,
        enterpriseId,
        serviceId,
    });
    const { rateGroupIds, rateIds } = getFilterViewResourceParams(filterValues.view);

    if (!(rateGroupIds || rateIds)) return null;

    const allResourcesCellData = allResourcesCategoryData?.data[timeUnit];
    const hasAllResourcesLimitReached =
        allResourcesCellData?.sold && allResourcesCellData?.absolutePerTimeUnitLimit
            ? allResourcesCellData.sold >= allResourcesCellData.absolutePerTimeUnitLimit
            : false;
    const isAllResourcesCategory = resourceCategoryId === allResourcesCategoryId;

    const resourceCategoryNames = resourceCategoryData?.resourceCategoryNames;
    const cellData = resourceCategoryData?.data[timeUnit];
    const {
        absolutePerTimeUnitLimit,
        freeResources,
        occupancy,
        rateGroupAbsolutePerTimeUnitLimit,
        sold,
        soldInRateGroup,
    } = cellData || {};
    const hasRateGroupLimitReached =
        filterValues.view.type === 'rate' && rateGroupAbsolutePerTimeUnitLimit && soldInRateGroup
            ? soldInRateGroup >= rateGroupAbsolutePerTimeUnitLimit
            : false;

    return (
        <Popover
            {...other}
            active
            onClose={closePopover}
            title={
                isAllResourcesCategory
                    ? lt('AllSpaces')
                    : resourceCategoryNames && <LocalizedProperty property={resourceCategoryNames} />
            }
            subtitle={viewTypeNames ? <LocalizedProperty property={viewTypeNames} /> : viewTypeName}
            position={Position.Top}
        >
            {sold != null ? (
                <FilterValuesContextProvider value={filterValues}>
                    <PopoverContent
                        absolutePerTimeUnitLimit={absolutePerTimeUnitLimit}
                        availableResources={freeResources || 0}
                        closePopover={closePopover}
                        enterpriseId={enterpriseId}
                        hasAllResourcesLimitReached={hasAllResourcesLimitReached}
                        hasRateGroupLimitReached={hasRateGroupLimitReached}
                        isAllResourcesCategory={isAllResourcesCategory}
                        occupancy={occupancy || 0}
                        rateGroupIds={rateGroupIds}
                        rateIds={rateIds}
                        resourceCategoryId={resourceCategoryId}
                        serviceId={serviceId}
                        sold={sold}
                        timeUnit={timeUnit}
                    />
                </FilterValuesContextProvider>
            ) : null}
            <div id={DIALOG_PORTAL_CONTAINER_ID} style={{ zIndex: ZIndex.Highest, position: 'absolute' }} />
            <OverlayedLoader show={isDataFetching} />
        </Popover>
    );
};
